import Link from "next/link";
import type { GetStaticProps } from "next";
import { allWordpressPosts } from "contentlayer/generated";
import BlogCard, { type BlogCardProps } from "../components/BlogCard";
import Hero from "../components/Hero";
import PricingCard from "../components/PricingCard";
import ServiceCard from "../components/ServiceCard";
import Seo from "../components/Seo";
import { useLineQuoteUrl } from "lib/line";
import { buildExcerpt, formatCategoryLabel, pickFallbackImage, parseGmtDate } from "../lib/posts";

const serviceData = [
  {
    id: "accounting",
    title: "รับทำบัญชีรายเดือน",
    description: "บันทึกบัญชี จัดทำรายงาน และยื่นแบบภาษีที่เกี่ยวข้องกับกิจการ เริ่มต้น 2,000 บาทต่อเดือน สำหรับเอกสารไม่เกิน 30 บิลต่อเดือน",
    items: [
      "จัดทำและยื่นแบบ ภ.ง.ด.1, 3, 53 และ ภ.พ.30 ตามที่เกี่ยวข้อง",
      "สรุปยอดขายและค่าใช้จ่ายประจำเดือน",
      "กระทบยอดบัญชีธนาคารจากเอกสารที่ได้รับ"
    ],
    icon: (
      <svg xmlns="http://www.w3.org/2000/svg" className="h-7 w-7" fill="none" viewBox="0 0 24 24" strokeWidth={1.7} stroke="currentColor">
        <path strokeLinecap="round" strokeLinejoin="round" d="M7 4h10a2 2 0 012 2v12a2 2 0 01-2 2H7a2 2 0 01-2-2V6a2 2 0 012-2z" />
        <path strokeLinecap="round" strokeLinejoin="round" d="M9 8h6" />
        <path strokeLinecap="round" strokeLinejoin="round" d="M9 12h6" />
        <path strokeLinecap="round" strokeLinejoin="round" d="M9 16h3" />
        <path strokeLinecap="round" strokeLinejoin="round" d="M9 4v2M15 4v2" />
      </svg>
    )
  },
  {
    id: "registration",
    title: "จดทะเบียนบริษัทและห้างหุ้นส่วน",
    description: "ดำเนินการจองชื่อ จัดเตรียมเอกสาร และยื่นจดทะเบียนธุรกิจ แพ็กเกจเริ่มต้น 9,500 บาท",
    items: [
      "จองชื่อและยื่นจดทะเบียนกับกรมพัฒนาธุรกิจการค้า",
      "จัดเตรียมเอกสารประกอบการจดทะเบียน",
      "ดำเนินการด้าน VAT และประกันสังคมเมื่อกิจการเข้าเงื่อนไข"
    ],
    icon: (
      <svg xmlns="http://www.w3.org/2000/svg" className="h-7 w-7" fill="none" viewBox="0 0 24 24" strokeWidth={1.7} stroke="currentColor">
        <path strokeLinecap="round" strokeLinejoin="round" d="M5 20h14" />
        <path strokeLinecap="round" strokeLinejoin="round" d="M7 20V9h10v11" />
        <path strokeLinecap="round" strokeLinejoin="round" d="M9 6l3-3 3 3" />
        <path strokeLinecap="round" strokeLinejoin="round" d="M12 12v4" />
        <path strokeLinecap="round" strokeLinejoin="round" d="M10 14h4" />
      </svg>
    )
  },
  {
    id: "annual",
    title: "ปิดงบและออกงบการเงิน",
    description: "จัดทำบัญชีเพื่อปิดรอบปี เตรียมงบการเงิน และประสานงานกับผู้สอบบัญชีรับอนุญาตก่อนนำส่งหน่วยงานที่เกี่ยวข้อง",
    items: [
      "ตรวจความครบถ้วนและปรับปรุงรายการบัญชีปลายปี",
      "จัดเตรียมงบการเงินและหมายเหตุประกอบงบ",
      "ประสานงานกับผู้สอบบัญชีและดำเนินการยื่นงบ"
    ],
    icon: (
      <svg xmlns="http://www.w3.org/2000/svg" className="h-7 w-7" fill="none" viewBox="0 0 24 24" strokeWidth={1.7} stroke="currentColor">
        <path strokeLinecap="round" strokeLinejoin="round" d="M7 3v2M17 3v2" />
        <path strokeLinecap="round" strokeLinejoin="round" d="M5 7h14v11a2 2 0 01-2 2H7a2 2 0 01-2-2V7z" />
        <path strokeLinecap="round" strokeLinejoin="round" d="M5 12h14" />
        <path strokeLinecap="round" strokeLinejoin="round" d="M12 15v4" />
        <path strokeLinecap="round" strokeLinejoin="round" d="M10.5 17.5L12 19l1.5-1.5" />
      </svg>
    )
  },
  {
    id: "payroll",
    title: "จัดทำเงินเดือนและประกันสังคม",
    description: "คำนวณเงินเดือน ภาษีหัก ณ ที่จ่าย และเงินสมทบประกันสังคม พร้อมจัดทำสลิปเงินเดือนตามข้อมูลที่กิจการส่งให้",
    items: [
      "คำนวณเงินเดือนและรายการหักของพนักงาน",
      "จัดทำข้อมูลภาษีเงินได้และประกันสังคม",
      "จัดทำสลิปเงินเดือนและเอกสารพนักงานที่เกี่ยวข้อง"
    ],
    icon: (
      <svg xmlns="http://www.w3.org/2000/svg" className="h-7 w-7" fill="none" viewBox="0 0 24 24" strokeWidth={1.7} stroke="currentColor">
        <path strokeLinecap="round" strokeLinejoin="round" d="M6 4h8l4 4v10a2 2 0 01-2 2H8a2 2 0 01-2-2V4z" />
        <path strokeLinecap="round" strokeLinejoin="round" d="M14 4v4h4" />
        <circle cx="12" cy="12" r="2" />
        <path strokeLinecap="round" strokeLinejoin="round" d="M9 18c0-1.657 1.343-3 3-3s3 1.343 3 3" />
      </svg>
    )
  },
  {
    id: "consult",
    title: "ตรวจทานภาษีและให้คำปรึกษา",
    description: "ทบทวนเอกสารและแบบภาษี ประเมินประเด็นที่ควรแก้ไข และช่วยเตรียมข้อมูลเมื่อกิจการได้รับหนังสือจากกรมสรรพากร",
    items: [
      "ตรวจทานเอกสารและแบบภาษีของกิจการ",
      "สรุปประเด็นที่ควรแก้ไขหรือเตรียมข้อมูลเพิ่มเติม",
      "ให้คำปรึกษาผ่าน LINE หรือนัดหมายเข้าพบ"
    ],
    icon: (
      <svg xmlns="http://www.w3.org/2000/svg" className="h-7 w-7" fill="none" viewBox="0 0 24 24" strokeWidth={1.7} stroke="currentColor">
        <path strokeLinecap="round" strokeLinejoin="round" d="M12 21c-4.8-1.9-8-5-8-8.9V6l8-3 8 3v6.1c0 3.9-3.2 7-8 8.9z" />
        <path strokeLinecap="round" strokeLinejoin="round" d="M9.5 12.5l2 2L15 11" />
      </svg>
    )
  },
  {
    id: "report",
    title: "รายงานสำหรับผู้บริหาร",
    description: "สรุปผลประกอบการ ฐานะการเงิน และกระแสเงินสด เพื่อให้ผู้บริหารติดตามตัวเลขสำคัญของกิจการได้สะดวกขึ้น",
    items: [
      "รายงานกำไรขาดทุน ฐานะการเงิน และกระแสเงินสด",
      "เปรียบเทียบผลการดำเนินงานในแต่ละเดือน",
      "สรุปรายงานภาษาไทยหรือภาษาอังกฤษตามขอบเขตงาน"
    ],
    icon: (
      <svg xmlns="http://www.w3.org/2000/svg" className="h-7 w-7" fill="none" viewBox="0 0 24 24" strokeWidth={1.7} stroke="currentColor">
        <path strokeLinecap="round" strokeLinejoin="round" d="M5 19h14" />
        <path strokeLinecap="round" strokeLinejoin="round" d="M8 19V9" />
        <path strokeLinecap="round" strokeLinejoin="round" d="M12 19V7" />
        <path strokeLinecap="round" strokeLinejoin="round" d="M16 19V5" />
      </svg>
    )
  }
];

const serviceLinkMap: Record<string, string> = {
  accounting: "/services/accounting",
  registration: "/services/registration",
  annual: "/services/accounting#pricing",
  payroll: "/services/accounting#monthly-services",
  consult: "/services/accounting#standards",
  report: "/services/accounting#checklist"
};

const pricingHighlight = [
  {
    name: "จดทะเบียนธุรกิจ",
    pricePrefix: "ราคาแพ็กเกจ",
    price: "9,500 บาท",
    description: "สำหรับจดทะเบียนบริษัทหรือห้างหุ้นส่วน โดยทีมงานจะแจ้งรายการเอกสารและขั้นตอนก่อนยื่นจดทะเบียน",
    features: [
      "จองชื่อและยื่นจดทะเบียนธุรกิจ",
      "จัดเตรียมเอกสารประกอบการจดทะเบียน",
      "ดำเนินการด้าน VAT และประกันสังคมเมื่อเข้าเงื่อนไข",
      "แจ้งสถานะและรายการที่ต้องดำเนินการหลังจดทะเบียน"
    ]
  },
  {
    name: "บัญชีรายเดือน",
    pricePrefix: "เริ่มต้น",
    price: "2,000 บาท",
    priceSuffix: "ต่อเดือน",
    description: "สำหรับกิจการที่มีเอกสารไม่เกิน 30 บิลต่อเดือน ค่าบริการอาจเปลี่ยนตามประเภทธุรกิจและแบบภาษีที่ต้องยื่น",
    features: [
      "จัดทำบัญชีจากเอกสารที่ได้รับ",
      "จัดทำและยื่นแบบภาษีที่เกี่ยวข้อง",
      "กระทบยอดบัญชีธนาคาร",
      "สรุปยอดขายและค่าใช้จ่ายประจำเดือน"
    ],
    popular: true
  },
  {
    name: "ปิดบัญชีประจำปี",
    pricePrefix: "เริ่มต้น",
    price: "9,000 บาท",
    description: "สำหรับกิจการที่ต้องจัดทำงบการเงินประจำปี ราคาขึ้นอยู่กับจำนวนรายการและความพร้อมของเอกสาร",
    features: [
      "ตรวจเอกสารและปรับปรุงรายการบัญชีปลายปี",
      "จัดเตรียมงบการเงินและหมายเหตุประกอบงบ",
      "ประสานงานกับผู้สอบบัญชีรับอนุญาต",
      "ดำเนินการยื่นงบและแบบภาษีที่เกี่ยวข้อง"
    ]
  }
];

const trustHighlights = [
  {
    title: "มีผู้ประสานงาน",
    description:
      "ติดต่อเรื่องเอกสาร กำหนดส่ง และสถานะงานผ่านช่องทางที่ตกลงร่วมกัน"
  },
  {
    title: "แจ้งรายการเอกสารล่วงหน้า",
    description:
      "ทีมงานแจ้งรายการเอกสารที่ต้องใช้และวันที่ควรส่งในแต่ละรอบบัญชี"
  },
  {
    title: "แจ้งขอบเขตและค่าใช้จ่าย",
    description:
      "หากมีงานนอกเหนือจากขอบเขตเดิม ทีมงานจะแจ้งรายละเอียดก่อนดำเนินการ"
  },
  {
    title: "ส่งเอกสารได้หลายช่องทาง",
    description:
      "ส่งเอกสารออนไลน์ หรือนัดหมายรับเอกสารตามพื้นที่และเงื่อนไขการให้บริการ"
  }
];

type HomePageProps = { blogPreview: BlogCardProps[] };

export default function HomePage({ blogPreview }: HomePageProps) {
  const title = "รับทำบัญชีและจดทะเบียนบริษัท เชียงใหม่";
  const lineUrl = useLineQuoteUrl();

  return (
    <>
      <Seo
        title={title}
        description="KMCP Accounting ให้บริการทำบัญชี ยื่นภาษี ปิดบัญชีประจำปี จัดทำเงินเดือน และจดทะเบียนธุรกิจในเชียงใหม่ ดูรายละเอียดบริการและค่าบริการเบื้องต้นได้ที่นี่"
        path="/"
      />
      <Hero />
      <section id="services" className="scroll-mt-24 bg-[#f7f8fa]">
        <div className="mx-auto max-w-7xl px-6 py-14 sm:px-8 lg:py-20">
          <div className="flex flex-col items-start justify-between gap-5 lg:flex-row lg:items-end">
            <div>
              <h2 className="max-w-2xl text-3xl font-semibold leading-[1.28] text-gray-950 sm:text-4xl sm:leading-tight">บริการบัญชี ภาษี และจดทะเบียนธุรกิจ</h2>
              <p className="mt-3 max-w-2xl text-base leading-7 text-slate-600">
                เลือกใช้บริการตามระยะของธุรกิจ ตั้งแต่เริ่มจดทะเบียน ดูแลงานบัญชีรายเดือน จัดทำเงินเดือน ไปจนถึงปิดบัญชีประจำปี
              </p>
            </div>
            <Link href="/services/accounting" className="btn-secondary">
              รายละเอียดบริการทั้งหมด
            </Link>
          </div>
          <div className="mt-8 grid grid-cols-1 gap-4 md:grid-cols-2 lg:mt-10 xl:grid-cols-3">
            {serviceData.map((service) => {
              const href = serviceLinkMap[service.id] ?? "/services/accounting";
              return (
                <ServiceCard
                  key={service.id}
                  icon={service.icon}
                  title={service.title}
                  description={service.description}
                  items={service.items}
                  href={href}
                />
              );
            })}
          </div>
        </div>
      </section>
      <section className="border-y border-slate-200 bg-white">
        <div className="mx-auto grid max-w-7xl grid-cols-1 px-6 sm:px-8 lg:grid-cols-3">
          {[
            ["01", "ส่งข้อมูลและเอกสาร", "ส่งเอกสารทางออนไลน์ หรือนัดหมายรับเอกสารตามพื้นที่ให้บริการ"],
            ["02", "ตรวจเอกสารและดำเนินงาน", "ทีมงานตรวจความครบถ้วน บันทึกบัญชี และจัดทำแบบภาษีตามขอบเขตบริการ"],
            ["03", "รับแบบภาษีและรายงาน", "รับเอกสารสำหรับชำระภาษีและรายงานประจำเดือน พร้อมติดตามรายการที่ต้องดำเนินการต่อ"]
          ].map(([number, title, description], index) => (
            <div key={number} className={`py-9 lg:px-8 lg:py-12 ${index > 0 ? "border-t border-slate-200 lg:border-l lg:border-t-0" : ""}`}>
              <span className="text-sm font-semibold text-[#9a7900]">{number}</span>
              <h3 className="mt-3 text-lg font-semibold text-gray-950">{title}</h3>
              <p className="mt-2 text-sm leading-7 text-slate-600">{description}</p>
            </div>
          ))}
        </div>
      </section>

      <section id="packages" className="bg-white">
        <div className="mx-auto max-w-7xl px-6 py-14 sm:px-8 lg:py-20">
          <div className="flex flex-col gap-6 sm:flex-row sm:items-center sm:justify-between">
            <div>
              <h2 className="text-3xl font-semibold text-gray-950 sm:text-4xl">ค่าบริการเบื้องต้น</h2>
              <p className="mt-4 max-w-2xl text-base leading-7 text-slate-600">
                ค่าบริการพิจารณาจากประเภทธุรกิจ จำนวนเอกสาร และงานที่ต้องดำเนินการเพิ่มเติม ทีมงานจะแจ้งขอบเขตและค่าใช้จ่ายก่อนเริ่มงาน
              </p>
            </div>
          </div>
          <div className="mt-9 grid grid-cols-1 gap-5 lg:grid-cols-3">
            {pricingHighlight.map((plan) => (
              <PricingCard
                key={plan.name}
                name={plan.name}
                price={plan.price}
                pricePrefix={plan.pricePrefix}
                priceSuffix={plan.priceSuffix}
                description={plan.description}
                features={plan.features}
                highlighted={plan.popular}
                cta={
                  <a href={lineUrl} target="_blank" rel="noreferrer" className={`min-h-12 w-full text-center ${plan.popular ? "inline-flex items-center justify-center rounded-full bg-gray-950 px-6 py-3 text-sm font-semibold text-white transition hover:bg-gray-800 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-gray-950 focus-visible:ring-offset-4" : "btn-primary"}`}>
                    สอบถามรายละเอียด
                  </a>
                }
              />
            ))}
          </div>
          <p className="mt-8 max-w-4xl text-sm leading-7 text-slate-500">
            ราคาข้างต้นเป็นราคาเบื้องต้น ขอบเขตงานและค่าใช้จ่ายจริงขึ้นอยู่กับประเภทธุรกิจ จำนวนเอกสาร สถานะบัญชี และบริการเพิ่มเติม กรุณาสอบถามรายละเอียดก่อนเริ่มงาน
          </p>
        </div>
      </section>

      <section className="relative overflow-hidden bg-[#fff9dc] text-gray-950">
        <div className="mx-auto max-w-7xl px-6 py-14 sm:px-8 lg:py-20">
          <div className="grid grid-cols-1 gap-8 lg:grid-cols-3 lg:gap-10">
            <div className="relative overflow-hidden lg:col-span-1">
              <h2 className="text-3xl font-semibold leading-tight text-gray-950 sm:text-4xl">ติดตามงานได้ตั้งแต่รับเอกสารจนถึงส่งรายงาน</h2>
              <p className="mt-5 text-base leading-8 text-slate-600">
                ทีมงานแจ้งรายการเอกสาร กำหนดส่ง และสถานะงานตามรอบบัญชี เพื่อให้ผู้ประกอบการทราบว่าต้องเตรียมอะไรในแต่ละเดือน
              </p>
            </div>
            <dl className="grid grid-cols-1 gap-x-10 gap-y-0 lg:col-span-2 lg:grid-cols-2">
              {trustHighlights.map((item) => (
                <div
                  key={item.title}
                  className="relative border-t border-[#eadb91] py-7"
                >
                  <dt className="text-lg font-semibold text-gray-950">{item.title}</dt>
                  <dd className="mt-3 text-sm leading-7 text-slate-600">{item.description}</dd>
                </div>
              ))}
            </dl>
          </div>
        </div>
      </section>
      <section className="bg-[#f7f8fa]">
        <div className="mx-auto max-w-7xl px-6 py-14 sm:px-8 lg:py-20">
          <div className="flex flex-col items-start justify-between gap-6 lg:flex-row lg:items-center">
            <div>
              <h2 className="text-3xl font-semibold text-gray-950 sm:text-4xl">บทความบัญชีและภาษี</h2>
              <p className="mt-4 text-base leading-7 text-slate-600">
                ข้อมูลเกี่ยวกับบัญชี ภาษี เงินเดือน และการจดทะเบียนธุรกิจสำหรับผู้ประกอบการ
              </p>
            </div>
            <Link href="/blog" className="btn-secondary">
              ดูบทความทั้งหมด
            </Link>
          </div>
          <div className="mt-9 grid grid-cols-1 gap-5 md:grid-cols-2 xl:grid-cols-3">
            {blogPreview.length === 0 ? (
              <p className="text-sm text-gray-600">ไม่มีบทความในขณะนี้ โปรดกลับมาใหม่ในภายหลัง</p>
            ) : (
              <>
                {blogPreview.map((post) => (
                  <BlogCard
                    key={post.href}
                    category={post.category}
                    title={post.title}
                    description={post.description}
                    href={post.href}
                    image={post.image}
                    imageAlt={post.imageAlt}
                    showCategory={false}
                  />
                ))}
              </>
            )}
          </div>
        </div>
      </section>
      <section className="bg-white">
        <div className="mx-auto max-w-7xl px-6 py-14 sm:px-8 lg:py-20">
          <div className="relative overflow-hidden rounded-3xl bg-brand px-6 py-10 text-gray-950 sm:px-10 sm:py-12 lg:px-14 lg:py-14">
            <div className="grid grid-cols-1 gap-10 lg:grid-cols-[1.5fr,1fr] lg:items-center">
              <div className="space-y-4">
                <h2 className="max-w-2xl text-3xl font-semibold leading-tight sm:text-4xl">สอบถามบริการและประเมินค่าบริการเบื้องต้น</h2>
                <p className="max-w-2xl text-base leading-7 text-gray-800">
                  แจ้งประเภทธุรกิจ จำนวนเอกสารโดยประมาณ และบริการที่ต้องการ ทีมงานจะช่วยสรุปขอบเขตงานและค่าใช้จ่ายเบื้องต้นให้
                </p>
              </div>
              <div className="relative flex flex-col items-center justify-center lg:items-end">
                <a
                  href="https://line.me/R/ti/p/@kmcp.acc"
                  className="inline-flex min-h-12 items-center justify-center rounded-full bg-[#111827] px-7 text-sm font-semibold text-white transition hover:bg-[#1f2937] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-gray-950 focus-visible:ring-offset-4 focus-visible:ring-offset-brand"
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  สอบถามผ่าน LINE <span aria-hidden className="ml-2">→</span>
                </a>
              </div>
            </div>
          </div>
        </div>
      </section>
    </>
  );
}

export const getStaticProps: GetStaticProps<HomePageProps> = async () => {
  const blogPreview = allWordpressPosts
    .filter((post) => post.status === "publish")
    .sort((a, b) => parseGmtDate(b.dateGmt || b.date).getTime() - parseGmtDate(a.dateGmt || a.date).getTime())
    .slice(0, 6)
    .map((post, index) => ({
      category: formatCategoryLabel(post.primaryCategory),
      title: post.title,
      description: buildExcerpt(post),
      href: `/blog/${post.seoSlug ?? post.slug}`,
      image: post.heroImage || pickFallbackImage(index),
      imageAlt: post.heroImageAlt || post.title
    }));

  return { props: { blogPreview } };
};
